/**
 * Sets a cookie with the specified name, value, and optional configuration.
 *
 * @param name - The name of the cookie to set.
 * @param value - The value to be stored in the cookie.
 * @param options - An optional object containing additional cookie options.
 * @param options.expires - An optional Date object specifying when the cookie should expire.
 * @param options.secure - Whether the cookie should only be sent over HTTPS (default: true in production).
 * @param options.sameSite - SameSite attribute for the cookie (default: 'lax').
 * @param options.httpOnly - Whether the cookie should be HTTP-only (default: false for client-side).
 * @param options.domain - The domain for the cookie (e.g., '.cruisenation.com' for subdomain support).
 * @returns void
 */
export const setCookie = (
  name: string, 
  value: string, 
  options: { 
    expires?: Date;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
    httpOnly?: boolean;
    domain?: string;
  } = {}
): void => {
  let cookieString = `${name}=${value}`;
  
  if (options.expires) {
    cookieString += `; expires=${options.expires.toUTCString()}`;
  }

  
  cookieString += '; path=/';
  
  // Set domain if provided
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  } else if (process.env.NODE_ENV === 'production') {
    cookieString += '; domain=.cruisenation.com';
  }
  
  // Set secure flag in production by default
  if (options.secure !== false && (options.secure || process.env.NODE_ENV === 'production')) {
    cookieString += '; secure';
  }
  
  // Set SameSite attribute
  const sameSite = options.sameSite || 'lax';
  cookieString += `; samesite=${sameSite}`;
  
  // Note: httpOnly can only be set server-side, so we ignore it here
  // but document it for server-side implementations
  
  document.cookie = cookieString;
};



/**
 * Retrieves the value of a specific cookie from the document's cookies.
 *
 * @param name - The name of the cookie to retrieve.
 * @returns The value of the specified cookie if found, or null if the cookie doesn't exist.
 */
export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue;
    }
  }
  return null;
};
